import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { RefreshCw } from 'lucide-react';
import { FlipkartCampaignBudgetModal } from '@/components/flipkart/FlipkartCampaignBudgetModal';
import {
  FlipkartCampaignDuplicateModal,
  type FlipkartCampaignDuplicateInput,
} from '@/components/flipkart/FlipkartCampaignDuplicateModal';
import { FlipkartCampaignEndDateModal } from '@/components/flipkart/FlipkartCampaignEndDateModal';
import {
  duplicateStepLabel,
  fsnsFromAdGroups,
} from '@/components/flipkart/flipkartCampaignDuplicate';
import { FlipkartPageHeader } from '@/components/flipkart/FlipkartPageHeader';
import { FlipkartPlacementBiddingModal } from '@/components/flipkart/FlipkartPlacementBiddingModal';
import { FlipkartSynopsisKpis } from '@/components/flipkart/FlipkartSynopsisKpis';
import { FlipkartSynopsisTable } from '@/components/flipkart/FlipkartSynopsisTable';
import {
  formatFlipkartPlacementToast,
  type FlipkartPlacementAdjustment,
} from '@/components/flipkart/flipkartPlacementBidding';
import {
  attachFlipkartAdGroupPlacements,
  attachFlipkartCampaignMeta,
  DEFAULT_FLIPKART_CAMPAIGN_STATUS,
  flipkartSynopsisLabel,
  flipkartWeekdayLabel,
  groupFlipkartSynopsisRows,
  summarizeFlipkartWindow,
  type FlipkartSynopsisGroup,
} from '@/components/flipkart/flipkartSynopsisMetrics';
import {
  flipkartEntitySyncCooldownFromDetail,
  flipkartEntitySyncRetrySeconds,
  formatFlipkartEntitySyncButtonLabel,
  formatFlipkartEntitySyncToast,
  formatFlipkartReportsSyncToast,
} from '@/components/flipkart/flipkartSyncMessages';
import {
  FLIPKART_ADS_ALL_ACCOUNTS_QUERY_KEY,
  flipkartAdsAccountsQueryKey,
  flipkartAdsAdGroupsQueryKey,
  flipkartAdsCampaignsQueryKey,
  flipkartAdsProductsQueryKey,
  flipkartAdsReportsQueryKey,
} from '@/components/FlipkartAdsOauthConnections';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { useFlipkartCampaignBudgetJob } from '@/hooks/useFlipkartCampaignBudgetJob';
import { useFlipkartCampaignEndDateJob } from '@/hooks/useFlipkartCampaignEndDateJob';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import type { SnapshotRangeState } from '@/components/snapshot/SnapshotDateRangePicker';
import {
  flipkartAdsApi,
  type FlipkartAdsAdGroup,
  type FlipkartAdsCampaign,
  type FlipkartAdsKeyword,
  type FlipkartAdsNegativeKeyword,
  type FlipkartAdsProduct,
  type FlipkartAdsEntitySyncStatus,
  type FlipkartAdsReportGrain,
  type FlipkartAdsReportRow,
} from '@/api/client';
import { flipkartAdAccountLabel } from '@/api/workspaceScope';

export { DEFAULT_FLIPKART_CAMPAIGN_STATUS };

function axiosErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && 'message' in detail) {
      return String((detail as { message?: string }).message || error.message);
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}

export type FlipkartKeywordTableRow = {
  id: string;
  kind: 'keyword' | 'negative';
  keyword_id: string;
  keyword_text: string | null;
  match_type: string | null;
  status: string | null;
  bid: number | null;
  campaign_id: string | null;
  ad_group_id: string | null;
};

export function flipkartCampaignStatusOptions(campaigns: FlipkartAdsCampaign[]): string[] {
  const values = new Set(campaigns.map((campaign) => campaign.status || 'Unknown'));
  values.add(DEFAULT_FLIPKART_CAMPAIGN_STATUS);
  return [...values].sort();
}

export function uniqueFlipkartStatusOptions(
  values: Array<string | null | undefined>
): string[] {
  return [...new Set(values.map((value) => value || 'Unknown'))].sort();
}

export function filterFlipkartCampaigns(
  campaigns: FlipkartAdsCampaign[],
  {
    query,
    status,
    campaignType,
  }: {
    query: string;
    status: string;
    campaignType: string;
  }
): FlipkartAdsCampaign[] {
  const needle = query.trim().toLowerCase();
  return campaigns.filter((campaign) => {
    if (status !== 'all' && (campaign.status || 'Unknown') !== status) return false;
    if (campaignType !== 'all' && (campaign.campaign_type || 'Unknown') !== campaignType) {
      return false;
    }
    if (!needle) return true;
    const haystack = [
      campaign.name,
      campaign.campaign_id,
      campaign.status,
      campaign.campaign_type,
      campaign.ad_account_id,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });
}

export function filterFlipkartAdGroups(
  rows: FlipkartAdsAdGroup[],
  { query, status }: { query: string; status: string }
): FlipkartAdsAdGroup[] {
  const needle = query.trim().toLowerCase();
  return rows.filter((row) => {
    if (status !== 'all' && (row.status || 'Unknown') !== status) return false;
    if (!needle) return true;
    return [row.name, row.ad_group_id, row.campaign_id, row.status, row.ui_status, row.cost_model]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(needle);
  });
}

export function mergeFlipkartKeywordRows(
  keywords: FlipkartAdsKeyword[],
  negatives: FlipkartAdsNegativeKeyword[]
): FlipkartKeywordTableRow[] {
  return [
    ...keywords.map((row) => ({
      id: row.id,
      kind: 'keyword' as const,
      keyword_id: row.keyword_id,
      keyword_text: row.keyword_text,
      match_type: row.match_type,
      status: row.status,
      bid: row.bid,
      campaign_id: row.campaign_id,
      ad_group_id: row.ad_group_id,
    })),
    ...negatives.map((row) => ({
      id: row.id,
      kind: 'negative' as const,
      keyword_id: row.negative_keyword_id,
      keyword_text: row.keyword_text,
      match_type: row.match_type,
      status: row.status,
      bid: null,
      campaign_id: row.campaign_id,
      ad_group_id: row.ad_group_id,
    })),
  ];
}

export function filterFlipkartKeywordRows(
  rows: FlipkartKeywordTableRow[],
  {
    query,
    status,
    kind,
  }: {
    query: string;
    status: string;
    kind: string;
  }
): FlipkartKeywordTableRow[] {
  const needle = query.trim().toLowerCase();
  return rows.filter((row) => {
    if (kind !== 'all' && row.kind !== kind) return false;
    if (status !== 'all' && (row.status || 'Unknown') !== status) return false;
    if (!needle) return true;
    return [row.keyword_text, row.keyword_id, row.match_type, row.campaign_id, row.ad_group_id]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(needle);
  });
}

export function flipkartAdGroupHasDetails(row: FlipkartAdsAdGroup): boolean {
  return Boolean(
    (row.cost_variation?.placements && row.cost_variation.placements.length > 0) ||
      (row.banners && row.banners.length > 0) ||
      (row.store_paths && row.store_paths.length > 0)
  );
}

export function filterFlipkartProducts(
  rows: FlipkartAdsProduct[],
  { query, status }: { query: string; status: string }
): FlipkartAdsProduct[] {
  const needle = query.trim().toLowerCase();
  return rows.filter((row) => {
    if (status !== 'all' && (row.status || 'Unknown') !== status) return false;
    if (!needle) return true;
    return [row.name, row.brand, row.product_id, row.fsn, row.sku, row.campaign_id, row.ad_group_id]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(needle);
  });
}

function unavailableMessage(title: string): string {
  return `${title} is not in synced Flipkart reports yet.`;
}

function scopedReportRows(
  rows: FlipkartAdsReportRow[] | undefined,
  adAccountId: string
): FlipkartAdsReportRow[] {
  return (rows ?? []).filter((row) => row.ad_account_id === adAccountId);
}

function withoutComparison(groups: FlipkartSynopsisGroup[]): FlipkartSynopsisGroup[] {
  return groups.map((group) => ({ ...group, hasPrevious: false }));
}

function campaignTypeKey(row: FlipkartAdsReportRow) {
  return flipkartSynopsisLabel(row.campaign_type);
}

function campaignIdKey(row: FlipkartAdsReportRow) {
  return row.campaign_id;
}

function campaignLabel(row: FlipkartAdsReportRow, key: string) {
  return flipkartSynopsisLabel(row.campaign_name, key);
}

function adGroupIdKey(row: FlipkartAdsReportRow) {
  return row.ad_group_id ?? null;
}

function adGroupLabel(row: FlipkartAdsReportRow, key: string) {
  return flipkartSynopsisLabel(row.ad_group_name, key);
}

function weekdayKey(row: FlipkartAdsReportRow) {
  return flipkartWeekdayLabel(row.date);
}

function targetingKey(row: FlipkartAdsReportRow) {
  return flipkartSynopsisLabel(row.keyword_text);
}

function matchTypeKey(row: FlipkartAdsReportRow) {
  return flipkartSynopsisLabel(row.match_type);
}

function searchTermKey(row: FlipkartAdsReportRow) {
  return flipkartSynopsisLabel(row.search_term);
}

function fsnKey(row: FlipkartAdsReportRow) {
  return row.fsn?.trim() || row.product_id?.trim() || null;
}

function fsnLabel(_row: FlipkartAdsReportRow, key: string) {
  return key;
}

function flipkartEntitySyncStatusQueryKey(credentialId: string) {
  return ['flipkart-ads', 'entity-sync-status', credentialId] as const;
}

export function FlipkartWorkspacePage() {
  const { credentialId: credentialIdParam, adAccountId: adAccountIdParam } = useParams<{
    credentialId: string;
    adAccountId: string;
  }>();
  const credentialId = credentialIdParam ? decodeURIComponent(credentialIdParam) : '';
  const adAccountId = adAccountIdParam ? decodeURIComponent(adAccountIdParam) : '';
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [placementOpen, setPlacementOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [selectedAdGroupIds, setSelectedAdGroupIds] = useState<string[]>([]);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const { range } = useAppDateRange();
  const endDateJob = useFlipkartCampaignEndDateJob(credentialId, adAccountId);
  const budgetJob = useFlipkartCampaignBudgetJob(credentialId, adAccountId);
  const campaignActionDisabled = endDateJob.isRunning || budgetJob.isRunning;

  const scoped = Boolean(credentialId && adAccountId);

  useEffect(() => {
    setSelectedAdGroupIds([]);
    setSelectedCampaignIds([]);
    setPlacementOpen(false);
    setEndDateOpen(false);
    setDuplicateOpen(false);
  }, [credentialId, adAccountId]);

  const entitySyncStatusQuery = useQuery({
    queryKey: flipkartEntitySyncStatusQueryKey(credentialId),
    queryFn: async () => {
      const res = await flipkartAdsApi.entitySyncStatus(credentialId);
      if (!res.success) throw new Error('Failed to load Flipkart entity sync status');
      return res.data;
    },
    enabled: Boolean(credentialId),
  });
  const entitySyncAvailableAt = entitySyncStatusQuery.data?.available_at ?? null;

  useEffect(() => {
    if (!entitySyncAvailableAt) return;
    const end = new Date(entitySyncAvailableAt).getTime();
    if (!Number.isFinite(end) || end <= Date.now()) return;
    const id = window.setInterval(() => {
      const next = Date.now();
      setNowMs(next);
      if (next >= end) window.clearInterval(id);
    }, 1000);
    return () => window.clearInterval(id);
  }, [entitySyncAvailableAt]);

  const entitySyncRetrySeconds = flipkartEntitySyncRetrySeconds(entitySyncAvailableAt, nowMs);

  const accountsQuery = useQuery({
    queryKey: flipkartAdsAccountsQueryKey(credentialId),
    queryFn: async () => {
      const res = await flipkartAdsApi.listAccounts(credentialId);
      if (!res.success) throw new Error('Failed to load Flipkart ad accounts');
      return res.data.accounts;
    },
    enabled: Boolean(credentialId),
  });

  const productsQuery = useQuery({
    queryKey: flipkartAdsProductsQueryKey(credentialId, adAccountId),
    queryFn: async () => {
      const res = await flipkartAdsApi.listProducts(credentialId, { adAccountId });
      if (!res.success) throw new Error('Failed to load Flipkart products');
      return res.data.products;
    },
    enabled: scoped,
  });

  const campaignsQuery = useQuery({
    queryKey: flipkartAdsCampaignsQueryKey(credentialId, adAccountId),
    queryFn: async () => {
      const res = await flipkartAdsApi.listCampaigns(credentialId, { adAccountId });
      if (!res.success) throw new Error('Failed to load Flipkart campaigns');
      return res.data.campaigns;
    },
    enabled: scoped,
  });

  const adGroupsQuery = useQuery({
    queryKey: flipkartAdsAdGroupsQueryKey(credentialId, adAccountId),
    queryFn: async () => {
      const res = await flipkartAdsApi.listAdGroups(credentialId, { adAccountId });
      if (!res.success) throw new Error('Failed to load Flipkart ad groups');
      return res.data.ad_groups;
    },
    enabled: scoped,
  });

  const campaignReportsQuery = useReportGrain(credentialId, adAccountId, 'campaigns', scoped);
  const adGroupReportsQuery = useReportGrain(credentialId, adAccountId, 'ad-groups', scoped);
  const keywordReportsQuery = useReportGrain(credentialId, adAccountId, 'keywords', scoped);
  const searchTermReportsQuery = useReportGrain(credentialId, adAccountId, 'search-terms', scoped);
  const productReportsQuery = useReportGrain(credentialId, adAccountId, 'products', scoped);

  const updateCampaignMutation = useMutation({
    mutationFn: async (input: {
      campaignId: string;
      patch: {
        daily_budget?: number;
        start_date?: string | null;
        end_date?: string | null;
      };
    }) => {
      const res = await flipkartAdsApi.updateCampaign(credentialId, input.campaignId, input.patch);
      if (!res.success) throw new Error('Failed to update Flipkart campaign');
      return res.data;
    },
    onSuccess: async () => {
      showToast('Campaign updated');
      await queryClient.invalidateQueries({ queryKey: flipkartAdsCampaignsQueryKey(credentialId) });
    },
    onError: (error) => {
      showToast(`Failed to update campaign: ${axiosErrorMessage(error)}`, 'error');
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async (input: FlipkartCampaignDuplicateInput & { campaignId: string }) => {
      const res = await flipkartAdsApi.duplicateCampaign(credentialId, input.campaignId, {
        name: input.name,
        start_date: input.startDate,
        end_date: input.endDate,
        product_ids: input.productIds,
        make_live: input.makeLive,
      });
      if (!res.success) throw new Error('Failed to duplicate Flipkart campaign');
      return res.data;
    },
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: flipkartAdsCampaignsQueryKey(credentialId) }),
        queryClient.invalidateQueries({ queryKey: flipkartAdsAdGroupsQueryKey(credentialId) }),
        queryClient.invalidateQueries({ queryKey: flipkartAdsProductsQueryKey(credentialId) }),
      ]);
      setSelectedCampaignIds([]);
      setDuplicateOpen(false);
      if (data.failed_step) {
        showToast(
          `Campaign created, but ${duplicateStepLabel(data.failed_step.step)} failed: ${data.failed_step.message}`,
          'error'
        );
        return;
      }
      showToast(
        data.status === 'LIVE' ? 'Campaign duplicated and set live' : 'Campaign duplicated as a draft'
      );
    },
  });

  const placementMutation = useMutation({
    mutationFn: async (adjustments: FlipkartPlacementAdjustment[]) => {
      const placements = Object.fromEntries(
        adjustments.map((item) => [item.placement, { mode: item.mode, value: item.value }])
      );
      const res = await flipkartAdsApi.updateAdGroupPlacements(credentialId, {
        ad_account_id: adAccountId,
        ad_group_ids: selectedAdGroupIds,
        placements,
      });
      if (!res.success) throw new Error('Failed to update placement bids');
      return res.data;
    },
    onSuccess: async (data) => {
      const failedOnly = data.applied.length === 0 && data.failed.length > 0;
      showToast(formatFlipkartPlacementToast(data), failedOnly ? 'error' : 'success');
      setPlacementOpen(false);
      setSelectedAdGroupIds([]);
      await queryClient.invalidateQueries({ queryKey: flipkartAdsAdGroupsQueryKey(credentialId) });
    },
    onError: (error) => {
      showToast(axiosErrorMessage(error), 'error');
    },
  });

  const syncCampaignsMutation = useMutation({
    mutationFn: async () => {
      const res = await flipkartAdsApi.syncCampaigns(credentialId);
      if (!res.success) throw new Error('Failed to sync Flipkart campaigns');
      return res.data;
    },
    onSuccess: async (data) => {
      showToast(`Synced ${data.upserted} of ${data.fetched} campaigns`);
      await queryClient.invalidateQueries({ queryKey: flipkartAdsCampaignsQueryKey(credentialId) });
    },
    onError: (error) => {
      showToast(axiosErrorMessage(error), 'error');
    },
  });

  const syncEntitiesMutation = useMutation({
    mutationFn: async () => {
      const res = await flipkartAdsApi.syncEntities(credentialId);
      if (!res.success) throw new Error('Failed to sync Flipkart campaigns, ad groups, keywords, and products');
      return res.data;
    },
    onSuccess: async (data) => {
      const availableAt = new Date(Date.now() + 30 * 60 * 1000).toISOString();
      queryClient.setQueryData<FlipkartAdsEntitySyncStatus>(
        flipkartEntitySyncStatusQueryKey(credentialId),
        { available_at: availableAt, retry_after_seconds: 30 * 60 }
      );
      setNowMs(Date.now());
      showToast(formatFlipkartEntitySyncToast(data));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: FLIPKART_ADS_ALL_ACCOUNTS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: flipkartAdsAccountsQueryKey(credentialId) }),
        queryClient.invalidateQueries({ queryKey: flipkartAdsCampaignsQueryKey(credentialId) }),
        queryClient.invalidateQueries({ queryKey: flipkartAdsProductsQueryKey(credentialId) }),
        queryClient.invalidateQueries({
          queryKey: flipkartEntitySyncStatusQueryKey(credentialId),
        }),
      ]);
    },
    onError: (error) => {
      const detail = axios.isAxiosError(error) ? error.response?.data?.detail : null;
      const cooldown = flipkartEntitySyncCooldownFromDetail(detail);
      if (cooldown) {
        queryClient.setQueryData(flipkartEntitySyncStatusQueryKey(credentialId), cooldown);
        setNowMs(Date.now());
      }
      showToast(axiosErrorMessage(error), 'error');
    },
  });

  const syncReportsMutation = useMutation({
    mutationFn: async () => {
      const res = await flipkartAdsApi.syncReports(credentialId);
      if (!res.success) throw new Error('Failed to sync Flipkart reports');
      return res.data;
    },
    onSuccess: async (data) => {
      showToast(formatFlipkartReportsSyncToast(data));
      await queryClient.invalidateQueries({ queryKey: flipkartAdsReportsQueryKey(credentialId) });
    },
    onError: (error) => {
      showToast(axiosErrorMessage(error), 'error');
    },
  });

  const account = accountsQuery.data?.find((item) => item.ad_account_id === adAccountId);
  const accountName = account ? flipkartAdAccountLabel(account) : adAccountId;
  const compare = range.compareEnabled;

  const campaignRows = useMemo(
    () => scopedReportRows(campaignReportsQuery.data, adAccountId),
    [adAccountId, campaignReportsQuery.data]
  );
  const adGroupRows = useMemo(
    () => scopedReportRows(adGroupReportsQuery.data, adAccountId),
    [adAccountId, adGroupReportsQuery.data]
  );
  const keywordRows = useMemo(
    () => scopedReportRows(keywordReportsQuery.data, adAccountId),
    [adAccountId, keywordReportsQuery.data]
  );
  const searchTermRows = useMemo(
    () => scopedReportRows(searchTermReportsQuery.data, adAccountId),
    [adAccountId, searchTermReportsQuery.data]
  );
  const productRows = useMemo(
    () => scopedReportRows(productReportsQuery.data, adAccountId),
    [adAccountId, productReportsQuery.data]
  );
  const productNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const product of productsQuery.data ?? []) {
      if (product.ad_account_id !== adAccountId || !product.name?.trim()) continue;
      if (product.fsn) names.set(product.fsn, product.name);
      names.set(product.product_id, product.name);
    }
    return names;
  }, [adAccountId, productsQuery.data]);

  const fsnSecondaryOf = useCallback(
    (row: FlipkartAdsReportRow) => {
      const fsn = row.fsn?.trim();
      const productId = row.product_id?.trim();
      if (fsn && productNames.has(fsn)) return productNames.get(fsn) ?? null;
      if (productId && productNames.has(productId)) return productNames.get(productId) ?? null;
      return null;
    },
    [productNames]
  );

  const campaignsById = useMemo(() => {
    const map = new Map<
      string,
      {
        status: string | null;
        daily_budget: number | null;
        start_date: string | null;
        end_date: string | null;
      }
    >();
    for (const campaign of campaignsQuery.data ?? []) {
      if (campaign.ad_account_id && campaign.ad_account_id !== adAccountId) continue;
      map.set(campaign.campaign_id, campaign);
    }
    return map;
  }, [adAccountId, campaignsQuery.data]);

  const campaignNamesById = useMemo(() => {
    const names = new Map<string, string>();
    for (const campaign of campaignsQuery.data ?? []) {
      if (campaign.ad_account_id && campaign.ad_account_id !== adAccountId) continue;
      const name = campaign.name?.trim();
      if (name) names.set(campaign.campaign_id, name);
    }
    return names;
  }, [adAccountId, campaignsQuery.data]);

  const adGroupCampaignNameOf = useCallback(
    (row: FlipkartAdsReportRow) => {
      const campaignId = row.campaign_id?.trim();
      if (!campaignId) return null;
      return campaignNamesById.get(campaignId) ?? null;
    },
    [campaignNamesById]
  );

  const adGroupsById = useMemo(() => {
    const map = new Map<string, FlipkartAdsAdGroup>();
    for (const row of adGroupsQuery.data ?? []) {
      if (row.ad_account_id && row.ad_account_id !== adAccountId) continue;
      if (row.ad_group_id) map.set(row.ad_group_id, row);
    }
    return map;
  }, [adAccountId, adGroupsQuery.data]);

  const duplicateCampaignId = selectedCampaignIds.length === 1 ? selectedCampaignIds[0] : null;
  const duplicateCampaign = duplicateCampaignId ? campaignsById.get(duplicateCampaignId) : undefined;
  const duplicateFsns = useMemo(
    () => (duplicateCampaignId ? fsnsFromAdGroups(adGroupsQuery.data ?? [], duplicateCampaignId) : []),
    [adGroupsQuery.data, duplicateCampaignId]
  );
  const selectableAdGroupIds = useMemo(() => new Set(adGroupsById.keys()), [adGroupsById]);
  const selectableCampaignIds = useMemo(() => new Set(campaignsById.keys()), [campaignsById]);

  const windows = useMemo(
    () => ({ current: range.current, previous: range.previous }),
    [range.current, range.previous]
  );

  const currentSummary = useMemo(
    () => summarizeFlipkartWindow(campaignRows, windows.current),
    [campaignRows, windows]
  );
  const previousSummary = useMemo(
    () => summarizeFlipkartWindow(campaignRows, windows.previous),
    [campaignRows, windows]
  );

  const campaignTypeGroups = useSynopsisGroups(
    campaignRows,
    windows,
    compare,
    campaignTypeKey
  );
  const campaignGroupsRaw = useSynopsisGroups(
    campaignRows,
    windows,
    compare,
    campaignIdKey,
    campaignLabel
  );
  const campaignGroups = useMemo(
    () => attachFlipkartCampaignMeta(campaignGroupsRaw, campaignsById),
    [campaignGroupsRaw, campaignsById]
  );
  const adGroupGroupsRaw = useSynopsisGroups(
    adGroupRows,
    windows,
    compare,
    adGroupIdKey,
    adGroupLabel,
    adGroupCampaignNameOf
  );
  const adGroupGroups = useMemo(
    () => attachFlipkartAdGroupPlacements(adGroupGroupsRaw, adGroupsById),
    [adGroupGroupsRaw, adGroupsById]
  );
  const weekdayGroups = useSynopsisGroups(campaignRows, windows, compare, weekdayKey);
  const targetingGroups = useSynopsisGroups(keywordRows, windows, compare, targetingKey);
  const matchTypeGroups = useSynopsisGroups(keywordRows, windows, compare, matchTypeKey);
  const searchTermGroups = useSynopsisGroups(searchTermRows, windows, compare, searchTermKey);
  const fsnGroups = useSynopsisGroups(
    productRows,
    windows,
    compare,
    fsnKey,
    fsnLabel,
    fsnSecondaryOf
  );

  if (!credentialId || !adAccountId) {
    return null;
  }

  const syncBusy =
    syncCampaignsMutation.isPending ||
    syncEntitiesMutation.isPending ||
    syncReportsMutation.isPending;

  return (
    <div className="mx-auto max-w-[1600px] space-y-3.5 px-6 pb-[18px] pt-1">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FlipkartPageHeader title="Workspace" accountName={accountName} adAccountId={adAccountId} />
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={syncBusy}
            onClick={() => syncReportsMutation.mutate()}
          >
            {syncReportsMutation.isPending ? (
              <Spinner className="h-3.5 w-3.5" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            )}
            Sync reports
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={syncBusy || entitySyncRetrySeconds > 0}
            onClick={() => {
              if (entitySyncRetrySeconds > 0) return;
              syncEntitiesMutation.mutate();
            }}
          >
            {syncEntitiesMutation.isPending ? (
              <Spinner className="h-3.5 w-3.5" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            )}
            {formatFlipkartEntitySyncButtonLabel(entitySyncRetrySeconds)}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={syncBusy}
            onClick={() => syncCampaignsMutation.mutate()}
          >
            {syncCampaignsMutation.isPending ? (
              <Spinner className="h-3.5 w-3.5" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            )}
            Sync campaigns
          </Button>
        </div>
      </div>
      <p className="text-body-sm text-foreground-muted">
        Spend, revenue, and efficiency for the selected dates, compared with the previous period.
      </p>

      {campaignReportsQuery.isError ? (
        <Card className="p-6 text-center">
          <p className="font-medium text-foreground">Could not load Flipkart campaign reports</p>
          <p className="mt-2 text-body-sm text-foreground-muted">
            {(campaignReportsQuery.error as Error).message}
          </p>
          <Button type="button" className="mt-4" onClick={() => void campaignReportsQuery.refetch()}>
            Retry
          </Button>
        </Card>
      ) : campaignReportsQuery.isLoading ? (
        <div className="flex min-h-[20vh] items-center justify-center">
          <Spinner className="h-8 w-8" />
        </div>
      ) : (
        <FlipkartSynopsisKpis
          current={currentSummary.metrics}
          previous={previousSummary.metrics}
          hasPrevious={compare && previousSummary.hasRows}
        />
      )}

      <FlipkartSynopsisTable
        title="Campaign Type"
        labelHeader="Type"
        groups={campaignTypeGroups}
        unavailableMessage={unavailableMessage('Campaign Type')}
        isLoading={campaignReportsQuery.isLoading}
        isError={campaignReportsQuery.isError}
        errorMessage={(campaignReportsQuery.error as Error | undefined)?.message}
        onRetry={() => void campaignReportsQuery.refetch()}
      />
      <FlipkartSynopsisTable
        title="Campaigns"
        labelHeader="Campaign Name"
        groups={campaignGroups}
        campaignMeta
        onUpdateCampaign={async (campaignId, patch) => {
          try {
            await updateCampaignMutation.mutateAsync({ campaignId, patch });
          } catch (error) {
            throw new Error(axiosErrorMessage(error));
          }
        }}
        unavailableMessage={unavailableMessage('Campaigns')}
        isLoading={campaignReportsQuery.isLoading}
        isError={campaignReportsQuery.isError}
        errorMessage={(campaignReportsQuery.error as Error | undefined)?.message}
        onRetry={() => void campaignReportsQuery.refetch()}
        selection={{
          selectedIds: selectedCampaignIds,
          selectableIds: selectableCampaignIds,
          unsyncedLabel: (label) => `Sync campaigns before editing ${label}`,
          onToggleId: (id, selected) => {
            setSelectedCampaignIds((current) => {
              if (selected) return current.includes(id) ? current : [...current, id];
              return current.filter((item) => item !== id);
            });
          },
          onToggleVisible: (ids, selected) => {
            setSelectedCampaignIds((current) => {
              if (selected) return [...new Set([...current, ...ids])];
              const hidden = new Set(ids);
              return current.filter((item) => !hidden.has(item));
            });
          },
          onClear: () => setSelectedCampaignIds([]),
          actions: [
            { id: 'change-end-date', label: 'Change end date' },
            { id: 'change-budget', label: 'Change budget' },
            {
              id: 'duplicate',
              label: 'Duplicate',
              disabled: selectedCampaignIds.length !== 1,
            },
          ],
          onAction: (actionId) => {
            if (actionId === 'change-end-date') {
              endDateJob.reset();
              setEndDateOpen(true);
            }
            if (actionId === 'change-budget') {
              budgetJob.reset();
              setBudgetOpen(true);
            }
            if (actionId === 'duplicate' && selectedCampaignIds.length === 1) {
              duplicateMutation.reset();
              setDuplicateOpen(true);
            }
          },
          actionDisabled: campaignActionDisabled || duplicateMutation.isPending,
        }}
      />
      <FlipkartSynopsisTable
        title="Ad Groups"
        labelHeader="Ad Group"
        secondaryHeader="Campaign Name"
        groups={adGroupGroups}
        showPlacements
        adGroupStatus
        selection={{
          selectedIds: selectedAdGroupIds,
          selectableIds: selectableAdGroupIds,
          onToggleId: (id, selected) => {
            setSelectedAdGroupIds((current) => {
              if (selected) return current.includes(id) ? current : [...current, id];
              return current.filter((item) => item !== id);
            });
          },
          onToggleVisible: (ids, selected) => {
            setSelectedAdGroupIds((current) => {
              if (selected) return [...new Set([...current, ...ids])];
              const hidden = new Set(ids);
              return current.filter((item) => !hidden.has(item));
            });
          },
          onClear: () => setSelectedAdGroupIds([]),
          unsyncedLabel: (label) => `Sync entities before changing placement for ${label}`,
          actions: [{ id: 'change-placement', label: 'Change placement' }],
          onAction: (actionId) => {
            if (actionId === 'change-placement') setPlacementOpen(true);
          },
          actionDisabled: placementMutation.isPending,
        }}
        unavailableMessage={unavailableMessage('Ad Groups')}
        isLoading={adGroupReportsQuery.isLoading}
        isError={adGroupReportsQuery.isError}
        errorMessage={(adGroupReportsQuery.error as Error | undefined)?.message}
        onRetry={() => void adGroupReportsQuery.refetch()}
      />
      <FlipkartSynopsisTable
        title="Placement"
        labelHeader="Placement"
        groups={null}
        unavailableMessage={unavailableMessage('Placement')}
      />
      <FlipkartSynopsisTable
        title="Page Type"
        labelHeader="Page Type"
        groups={null}
        unavailableMessage={unavailableMessage('Page Type')}
      />
      <FlipkartSynopsisTable
        title="Weekday"
        labelHeader="Weekday"
        groups={weekdayGroups}
        unavailableMessage={unavailableMessage('Weekday')}
        isLoading={campaignReportsQuery.isLoading}
        isError={campaignReportsQuery.isError}
        errorMessage={(campaignReportsQuery.error as Error | undefined)?.message}
        onRetry={() => void campaignReportsQuery.refetch()}
      />
      <FlipkartSynopsisTable
        title="Hourly Trends"
        labelHeader="Hour"
        groups={null}
        unavailableMessage={unavailableMessage('Hourly Trends')}
      />
      <FlipkartSynopsisTable
        title="Category"
        labelHeader="Category"
        groups={null}
        unavailableMessage={unavailableMessage('Category')}
      />
      <FlipkartSynopsisTable
        title="Sub-Category"
        labelHeader="Sub-Category"
        groups={null}
        unavailableMessage={unavailableMessage('Sub-Category')}
      />
      <FlipkartSynopsisTable
        title="Targeting"
        labelHeader="Targeting"
        groups={targetingGroups}
        unavailableMessage={unavailableMessage('Targeting')}
        isLoading={keywordReportsQuery.isLoading}
        isError={keywordReportsQuery.isError}
        errorMessage={(keywordReportsQuery.error as Error | undefined)?.message}
        onRetry={() => void keywordReportsQuery.refetch()}
      />
      <FlipkartSynopsisTable
        title="Match Type"
        labelHeader="Match Type"
        groups={matchTypeGroups}
        unavailableMessage={unavailableMessage('Match Type')}
        isLoading={keywordReportsQuery.isLoading}
        isError={keywordReportsQuery.isError}
        errorMessage={(keywordReportsQuery.error as Error | undefined)?.message}
        onRetry={() => void keywordReportsQuery.refetch()}
      />
      <FlipkartSynopsisTable
        title="Search Term"
        labelHeader="Search Term"
        groups={searchTermGroups}
        unavailableMessage={unavailableMessage('Search Term')}
        isLoading={searchTermReportsQuery.isLoading}
        isError={searchTermReportsQuery.isError}
        errorMessage={(searchTermReportsQuery.error as Error | undefined)?.message}
        onRetry={() => void searchTermReportsQuery.refetch()}
      />
      <FlipkartSynopsisTable
        title="FSN ID"
        labelHeader="FSN ID"
        secondaryHeader="Product Name"
        groups={fsnGroups}
        unavailableMessage={unavailableMessage('FSN ID')}
        isLoading={productReportsQuery.isLoading}
        isError={productReportsQuery.isError}
        errorMessage={(productReportsQuery.error as Error | undefined)?.message}
        onRetry={() => void productReportsQuery.refetch()}
      />
      <FlipkartSynopsisTable
        title="Brand vs Non Brand"
        labelHeader="Type"
        groups={null}
        unavailableMessage={unavailableMessage('Brand vs Non Brand')}
      />
      {placementOpen ? (
        <FlipkartPlacementBiddingModal
          selectionCount={selectedAdGroupIds.length}
          pending={placementMutation.isPending}
          onCancel={() => {
            if (!placementMutation.isPending) setPlacementOpen(false);
          }}
          onConfirm={(adjustments) => {
            placementMutation.mutate(adjustments);
          }}
        />
      ) : null}
      {endDateOpen ? (
        <FlipkartCampaignEndDateModal
          selectionCount={selectedCampaignIds.length}
          pending={endDateJob.isRunning}
          job={endDateJob.job}
          failedResults={endDateJob.failedResults}
          error={
            endDateJob.error
              ? axiosErrorMessage(endDateJob.error)
              : null
          }
          onCancel={() => {
            if (endDateJob.isRunning) return;
            const finished = endDateJob.job != null;
            setEndDateOpen(false);
            endDateJob.reset();
            if (finished) {
              setSelectedCampaignIds([]);
              void queryClient.invalidateQueries({
                queryKey: flipkartAdsCampaignsQueryKey(credentialId),
              });
            }
          }}
          onConfirm={(endDate) => {
            void endDateJob.start({
              adAccountId,
              campaignIds: selectedCampaignIds,
              endDate,
            });
          }}
          onRetryFailed={() => {
            if (!endDateJob.job) return;
            void endDateJob.retryFailed(endDateJob.job.id);
          }}
        />
      ) : null}
      {budgetOpen ? (
        <FlipkartCampaignBudgetModal
          selectionCount={selectedCampaignIds.length}
          pending={budgetJob.isRunning}
          job={budgetJob.job}
          failedResults={budgetJob.failedResults}
          error={budgetJob.error ? axiosErrorMessage(budgetJob.error) : null}
          onCancel={() => {
            if (budgetJob.isRunning) return;
            const finished = budgetJob.job != null;
            setBudgetOpen(false);
            budgetJob.reset();
            if (finished) {
              setSelectedCampaignIds([]);
              void queryClient.invalidateQueries({
                queryKey: flipkartAdsCampaignsQueryKey(credentialId),
              });
            }
          }}
          onConfirm={(adjustMode, adjustValue) => {
            void budgetJob.start({
              adAccountId,
              campaignIds: selectedCampaignIds,
              adjustMode,
              adjustValue,
            });
          }}
          onRetryFailed={() => {
            if (!budgetJob.job) return;
            void budgetJob.retryFailed(budgetJob.job.id);
          }}
        />
      ) : null}
      {duplicateOpen && duplicateCampaignId ? (
        <FlipkartCampaignDuplicateModal
          sourceName={campaignNamesById.get(duplicateCampaignId) ?? null}
          startDate={duplicateCampaign?.start_date ?? null}
          endDate={duplicateCampaign?.end_date ?? null}
          fsns={duplicateFsns}
          productNames={productNames}
          pending={duplicateMutation.isPending}
          error={duplicateMutation.error ? axiosErrorMessage(duplicateMutation.error) : null}
          onCancel={() => {
            if (duplicateMutation.isPending) return;
            setDuplicateOpen(false);
            duplicateMutation.reset();
          }}
          onConfirm={(input) => {
            duplicateMutation.mutate({ ...input, campaignId: duplicateCampaignId });
          }}
        />
      ) : null}
    </div>
  );
}

function useReportGrain(
  credentialId: string,
  adAccountId: string,
  grain: FlipkartAdsReportGrain,
  enabled: boolean
) {
  return useQuery({
    queryKey: flipkartAdsReportsQueryKey(credentialId, grain, adAccountId),
    queryFn: async () => {
      const res = await flipkartAdsApi.listReports(credentialId, grain, { adAccountId });
      if (!res.success) throw new Error(`Failed to load Flipkart ${grain} reports`);
      return res.data.rows;
    },
    enabled,
  });
}

function useSynopsisGroups(
  rows: FlipkartAdsReportRow[],
  windows: { current: SnapshotRangeState['current']; previous: SnapshotRangeState['previous'] },
  compare: boolean,
  keyOf: (row: FlipkartAdsReportRow) => string | null,
  labelOf?: (row: FlipkartAdsReportRow, key: string) => string,
  secondaryOf?: (row: FlipkartAdsReportRow, key: string) => string | null
): FlipkartSynopsisGroup[] {
  return useMemo(() => {
    const groups = groupFlipkartSynopsisRows(rows, {
      current: windows.current,
      previous: windows.previous,
      keyOf,
      labelOf,
      secondaryOf,
    });
    return compare ? groups : withoutComparison(groups);
  }, [compare, keyOf, labelOf, rows, secondaryOf, windows]);
}
